'use client';

import { useClerk, useAuth as useClerkAuth, useUser as useClerkUser } from '@clerk/nextjs';
import { signOut as firebaseSignOut, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { AuthState, UserInfo } from './types';
import type { User as FirebaseUser } from 'firebase/auth';
import type { ReactNode } from 'react';
import { globalStore } from '@/app/globalStore';
import { auth } from '@/firebase/config';
import { useAuthToken } from '@/hooks/useAuthToken';
import { requestSentryNextjsUserIdentity } from '@/lib/sentry/applySentryVersionTags';

/** Adapter to make FirebaseUser compatible with AuthUser interface */
function firebaseUserToAuthUser(user: FirebaseUser): AuthState['user'] {
  return {
    sub: user.uid,
    sandboxUserId: user.uid,
    username: user.displayName ?? user.email ?? user.uid,
    email: user.email,
    imageUrl: user.photoURL ?? '',
    roles: []
  };
}
const AuthContext = createContext<AuthState | null>(null);
export function ClerkAuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null | undefined>(undefined);
  const {
    getToken: getClerkToken,
    userId: clerkUserId,
    isLoaded: clerkIsLoaded
  } = useClerkAuth();
  const {
    user: clerkUser = null
  } = useClerkUser();
  const {
    signOut: clerkSignOut
  } = useClerk();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const tokenSettingInProgress = useRef(false);
  const tokenSettingRetries = useRef(0);
  const MAX_TOKEN_RETRIES = 3;
  const {
    refreshAndSetToken,
    clearAuthToken
  } = useAuthToken();
  const isAuthReady = clerkIsLoaded && firebaseInitialized;
  useEffect(() => {
    if (clerkIsLoaded) {
      console.log('[auth] Clerk ready:', {
        hasUser: !!clerkUserId
      });
    }
  }, [clerkIsLoaded, clerkUserId]);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth(), async user => {
      try {
        console.log('[auth] Firebase auth state changed:', !!user);
        setFirebaseUser(user);
        setFirebaseInitialized(true);
      } catch (error) {
        console.error('[auth] Auth state change error:', error);
        setFirebaseInitialized(true);
      }
    });
    return () => unsubscribe();
  }, []);
  const login = useCallback(async () => {
    if (!clerkUserId || isLoggingIn || isLoggingOut) return;
    console.log('[auth] Logging in...');
    setIsLoggingIn(true);
    try {
      const clerkToken = await getClerkToken();
      if (!clerkToken) {
        console.error('[auth] Failed to get Clerk token');
        return;
      }
      const response = await fetch('/api/auth/firebase-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: clerkToken
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error ?? 'Failed to get Firebase token');
      }
      const {
        firebaseToken
      } = await response.json();
      if (firebaseToken) {
        await signInWithCustomToken(auth(), firebaseToken);
        console.log('[auth] Successfully logged into Firebase.');
      }
    } catch (error_0) {
      console.error('[auth] Login error:', error_0);
    } finally {
      setIsLoggingIn(false);
    }
  }, [clerkUserId, isLoggingIn, isLoggingOut, getClerkToken]);
  useEffect(() => {
    if (!isAuthReady) return;
    if (clerkUserId && firebaseUser === null && !isLoggingIn) {
      console.log('[auth] Auto-login triggered.');
      login();
    }
  }, [clerkUserId, firebaseUser, login, isAuthReady, isLoggingIn]);
  const logout = useCallback(async () => {
    if (isLoggingIn || isLoggingOut) return;
    console.log('[auth] Logging out...');
    setIsLoggingOut(true);
    tokenSettingRetries.current = 0;
    tokenSettingInProgress.current = false;
    try {
      await Promise.all([clerkSignOut({
        redirectUrl: '/auth/signin'
      }), firebaseSignOut(auth())]);
      console.log('[auth] Successfully logged out.');
    } catch (error_1) {
      console.error('[auth] Logout error:', error_1);
    } finally {
      setIsLoggingOut(false);
    }
  }, [isLoggingIn, isLoggingOut, clerkSignOut]);
  useEffect(() => {
    if (!isAuthReady) return;
    if (firebaseUser && !clerkUserId && !isLoggingOut && !isLoggingIn) {
      console.log('[auth] Firebase authenticated but Clerk session missing - logging out.');
      logout();
    }
  }, [firebaseUser, clerkUserId, isLoggingOut, isLoggingIn, logout, isAuthReady]);
  const setTokenWithRetry = useCallback(async (user_0: FirebaseUser, attempt = 0): Promise<boolean> => {
    if (attempt >= MAX_TOKEN_RETRIES) {
      console.error('[auth] Max token setting retries exceeded.');
      return false;
    }
    try {
      const token = await refreshAndSetToken(user_0);
      if (token) {
        tokenSettingRetries.current = 0;
        return true;
      }
      throw new Error('Failed to set token');
    } catch {
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
      console.log(`[auth] Token setting failed (attempt ${attempt + 1}/${MAX_TOKEN_RETRIES}), retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return setTokenWithRetry(user_0, attempt + 1);
    }
  }, [refreshAndSetToken]);
  useEffect(() => {
    if (!firebaseUser) return;
    const refreshInterval = setInterval(async () => {
      try {
        await refreshAndSetToken(firebaseUser);
        setTokenVerified(true);
        console.log('[auth] Successfully refreshed auth token.');
      } catch (error_2) {
        setTokenVerified(false);
        console.error('[auth] Token refresh failed:', error_2);
      }
    }, 50 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [firebaseUser, refreshAndSetToken]);
  useEffect(() => {
    const updateAuthToken = async () => {
      if (!isAuthReady) return;
      if (tokenSettingInProgress.current) return;
      try {
        if (firebaseUser) {
          tokenSettingInProgress.current = true;
          const success = await setTokenWithRetry(firebaseUser);
          setTokenVerified(success);
        } else {
          setTokenVerified(false);
          await clearAuthToken();
        }
      } catch (error_3) {
        console.error('[auth] Failed to update auth token:', error_3);
        setTokenVerified(false);
      } finally {
        tokenSettingInProgress.current = false;
      }
    };
    updateAuthToken();
  }, [firebaseUser, clearAuthToken, setTokenWithRetry, isAuthReady]);
  useEffect(() => {
    const updateUserInfo = async () => {
      if (!isAuthReady) return;
      try {
        if (!firebaseUser) {
          setUserInfo(null);
          globalStore.setCurrentUser(null, null);
          requestSentryNextjsUserIdentity(null, null);
          return;
        }
        let firebaseEmail = firebaseUser.email;
        if (!firebaseEmail) {
          try {
            const token_0 = await firebaseUser.getIdTokenResult();
            firebaseEmail = token_0.claims.email as string || null;
          } catch {
            // Ignore
          }
        }
        let newUserInfo: UserInfo = {
          userName: firebaseUser.displayName,
          email: firebaseEmail,
          imageUrl: firebaseUser.photoURL,
          fullName: firebaseUser.displayName
        };
        if (clerkUser) {
          const clerkUserName = clerkUser.username ?? clerkUser.fullName ?? clerkUser.firstName ?? (clerkUser.emailAddresses.length > 0 ? clerkUser.emailAddresses[0].emailAddress : null);
          newUserInfo = {
            userName: newUserInfo.userName ?? clerkUserName,
            email: newUserInfo.email ?? (clerkUser.emailAddresses.length > 0 ? clerkUser.emailAddresses[0].emailAddress : null),
            imageUrl: newUserInfo.imageUrl ?? (clerkUser.externalAccounts.length > 0 ? clerkUser.externalAccounts[0].imageUrl : null),
            fullName: newUserInfo.fullName ?? clerkUser.fullName
          };
        }
        const hasChanged = !userInfo || userInfo.userName !== newUserInfo.userName || userInfo.email !== newUserInfo.email || userInfo.imageUrl !== newUserInfo.imageUrl || userInfo.fullName !== newUserInfo.fullName;
        if (hasChanged) {
          setUserInfo(newUserInfo);
          globalStore.setCurrentUser(firebaseUser.uid, newUserInfo);
        }
        requestSentryNextjsUserIdentity(firebaseUser.uid, newUserInfo.fullName ?? newUserInfo.userName);
      } catch (error_4) {
        console.error('[auth] Failed to update user info:', error_4);
      }
    };
    updateUserInfo();
  }, [firebaseUser, clerkUser, userInfo, isAuthReady]);
  const getAccessToken = useCallback(async () => {
    if (!firebaseUser) throw new Error('[auth] no access token; user not signed in');
    return firebaseUser.getIdToken();
  }, [firebaseUser]);
  const user_1 = firebaseUser ? firebaseUserToAuthUser(firebaseUser) : null;
  const value: AuthState = {
    isLoading: !isAuthReady,
    isAuthenticated: !!firebaseUser,
    isLoggingOut,
    user: user_1,
    firebaseUser: user_1,
    userInfo,
    getAccessToken,
    login,
    logout,
    isLoggingIn,
    tokenVerified,
    isAuthReady
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useClerkAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useClerkAuthContext must be used within ClerkAuthProvider");
  }
  return ctx;
}
export { AuthContext as ClerkAuthContext };